import { EVENTS } from "@archetype-themes/utils/events";

class EngravingInfo extends HTMLElement {
  constructor() {
    super();

    // Centralised selectors for easier maintenance
    this.selectors = {
      container: "#lf-engraving-info-container",
      singleContents: ".lf-engraving-info-single-contents",
      doubleContents: ".lf-engraving-info-double-contents",
      sideBInputWrapper: "#lf-engraving-info-field-wrapper-side-b",
      sideBInputs: "#lf-engraving-info-field-wrapper-side-b input",
      rowlieLogo: "#lf-engraving-info-logo",
      addToCartButtons: ".add-to-cart",
      sideAWarning: "#lf-side-a-options-warning",
      sideBWarning: "#lf-side-b-options-warning",
      acceleratedCheckout: "shopify-accelerated-checkout",
    };

    // Dynamically passed data from Liquid
    this.formId = this.dataset.formId;
    this.translationStrings = JSON.parse(this.dataset.translations || "{}");

    // Enable debug mode for development
    this.debugMode = false;

    // Debugging initialisation
    this.debugMode &&
      console.log("[EngravingInfo] Initialising with formId:", this.formId);

    // Setup listeners and initial state
    this.setupEventListeners();
    this.handleAtcButtonActivity();
  }

  connectedCallback() {
    this.debugMode && console.log("[EngravingInfo] Component connected");

    // AbortController to clean up event listeners on disconnection
    this.abortController = new AbortController();

    // Listen for variant changes and handle updates
    document.addEventListener(
      `${EVENTS.variantChange}:${this.dataset.sectionId}:${this.dataset.productId}`,
      this.handleVariantChange.bind(this),
      { signal: this.abortController.signal }
    );
  }

  disconnectedCallback() {
    this.debugMode && console.log("[EngravingInfo] Component disconnected");
    this.abortController.abort(); // Clean up event listeners
  }

  setupEventListeners() {
    this.debugMode && console.log("[EngravingInfo] Setting up event listeners");

    // Input field listeners
    const idInputs = Array.from(
      document.getElementsByClassName("lf-engraving-info-id-field")
    );
    idInputs.forEach((input) => {
      input.addEventListener("focus", this.hidePlaceholders.bind(this));
      input.addEventListener("focusout", this.validateInput.bind(this));
      input.addEventListener("keyup", this.validateInput.bind(this));
    });

    // Add to Cart button listeners
    const formWrapper = document.getElementById(this.formId);
    const buttonElements =
      formWrapper?.querySelectorAll(this.selectors.addToCartButtons) || [];
    buttonElements.forEach((button) =>
      button.addEventListener("click", this.handleAddToCartClick.bind(this))
    );
  }

  handleVariantChange({ detail }) {
    this.debugMode && console.log("[EngravingInfo] Handling variant change");
    const { variant } = detail;
    if (!variant) {
      this.debugMode && console.warn("[EngravingInfo] Variant not found");
      return;
    }

    // Update the engraving fields based on the new variant SKU
    this.updateEngravingFields(variant.sku);

    // Recheck regex errors after variant change
    this.debugMode &&
      console.log(
        "[EngravingInfo] Rechecking regex errors after variant change"
      );
    this.handleRegexErrors();
  }

  updateEngravingFields(sku) {
    this.debugMode &&
      console.log("[EngravingInfo] Updating engraving fields for SKU:", sku);

    if (!sku) {
      this.debugMode &&
        console.warn("[EngravingInfo] SKU is undefined or invalid");
      return;
    }

    const singleEls = document.querySelectorAll(this.selectors.singleContents);
    const doubleEls = document.querySelectorAll(this.selectors.doubleContents);
    const sideBInputEls = document.querySelectorAll(this.selectors.sideBInputs);
    const rowlieLogoEl = document.querySelector(this.selectors.rowlieLogo);

    if (sku.includes("single")) {
      this.toggleVisibility(singleEls, doubleEls, rowlieLogoEl, "single");
    } else if (sku.includes("double")) {
      this.toggleVisibility(
        doubleEls,
        singleEls,
        rowlieLogoEl,
        "double",
        sideBInputEls
      );
    } else if (sku.includes("rowlie")) {
      this.toggleVisibility(singleEls, doubleEls, rowlieLogoEl, "rowlie");
    } else {
      this.toggleVisibility(
        [],
        singleEls.concat(doubleEls),
        rowlieLogoEl,
        "unknown"
      );
    }

    // Reset error states and recheck button activity
    this.cancelErrors();
    this.handleAtcButtonActivity();
  }

  toggleVisibility(showEls, hideEls, logoEl, type, sideBInputEls = []) {
    this.debugMode &&
      console.log(`[EngravingInfo] Toggling visibility for type: ${type}`);

    showEls.forEach((el) => el.classList.remove("hide"));
    hideEls.forEach((el) => el.classList.add("hide"));

    if (type === "rowlie") {
      logoEl?.classList.remove("hide");
    } else {
      logoEl?.classList.add("hide");
    }

    if (type === "double") {
      sideBInputEls.forEach((el, i) => {
        el.name = `properties[Side B - Line ${i + 1}]`;
      });
    } else {
      sideBInputEls.forEach((el) => {
        el.value = "";
        el.name = "";
      });
    }
  }

  handleAddToCartClick(evt) {
    this.debugMode && console.log("[EngravingInfo] Add to Cart clicked");

    const blankFound = this.checkBlank();
    const regexErrorFound = this.checkRegex();

    if (blankFound.length || regexErrorFound.length) {
      evt.preventDefault();
      blankFound.length && this.handleBlankErrors();
      regexErrorFound.length && this.handleRegexErrors();
    }
  }

  cancelErrors() {
    this.debugMode && console.log("[EngravingInfo] Cancelling errors");

    const sideAWarningEl = document.querySelector(this.selectors.sideAWarning);
    const sideBWarningEl = document.querySelector(this.selectors.sideBWarning);

    sideAWarningEl.innerHTML = "";
    sideAWarningEl.classList.add("hide");
    sideBWarningEl.innerHTML = "";
    sideBWarningEl.classList.add("hide");
  }

  handleRegexErrors() {
    this.debugMode && console.log("[EngravingInfo] Handling regex errors");

    const regexErrorFound = this.checkRegex();

    this.debugMode &&
      console.log("[EngravingInfo] Regex report", { regexErrorFound });

    this.updateErrorState(
      regexErrorFound.includes("A"),
      this.selectors.sideAWarning,
      this.translationStrings.invalidCharsWarning
    );

    this.updateErrorState(
      regexErrorFound.includes("B"),
      this.selectors.sideBWarning,
      this.translationStrings.invalidCharsWarning
    );
  }

  handleBlankErrors() {
    this.debugMode && console.log("[EngravingInfo] Handling blank errors");

    const blankFound = this.checkBlank();

    this.updateErrorState(
      blankFound.includes("A"),
      this.selectors.sideAWarning,
      this.translationStrings.blankTextWarning
    );

    this.updateErrorState(
      blankFound.includes("B"),
      this.selectors.sideBWarning,
      this.translationStrings.blankTextWarning
    );
  }

  updateErrorState(hasError, selector, message) {
    const errorEl = document.querySelector(selector);
    if (hasError) {
      errorEl.innerHTML = message;
      errorEl.classList.remove("hide");
    } else {
      errorEl.innerHTML = "";
      errorEl.classList.add("hide");
    }
  }

  checkBlank() {
    const blankFound = [];
    const sideAInputs = document.querySelectorAll(
      'input[name^="properties[Side A -"]'
    );

    if (!Array.from(sideAInputs).some((input) => input.value.trim())) {
      blankFound.push("A");
    }

    const sideBInputs = document.querySelectorAll(this.selectors.sideBInputs);
    if (
      !document
        .querySelector(this.selectors.sideBInputWrapper)
        .classList.contains("hide") &&
      !Array.from(sideBInputs).some((input) => input.value.trim())
    ) {
      blankFound.push("B");
    }

    return blankFound;
  }

  checkRegex() {
    const regexErrorFound = [];
    const regexpAlphaNumericPunctuation = new RegExp(
      "^(?!.*[\\u{1F600}-\\u{1F64F}\\u{1F300}-\\u{1F5FF}\\u{1F680}-\\u{1F6FF}\\u{2600}-\\u{26FF}\\u{2700}-\\u{27BF}\\u{1F900}-\\u{1F9FF}\\u{1FA70}-\\u{1FAFF}\\u{1F1E6}-\\u{1F1FF}])[\\p{L}\\p{N}\\p{P}\\p{S}\\p{Z}]*$",
      "u"
    );

    const sideAInputs = document.querySelectorAll(
      'input[name^="properties[Side A -"]'
    );
    if (
      !regexpAlphaNumericPunctuation.test(
        Array.from(sideAInputs)
          .map((input) => input.value)
          .join("")
      )
    ) {
      regexErrorFound.push("A");
    }

    const sideBInputs = document.querySelectorAll(this.selectors.sideBInputs);
    if (
      !document
        .querySelector(this.selectors.sideBInputWrapper)
        .classList.contains("hide") &&
      !regexpAlphaNumericPunctuation.test(
        Array.from(sideBInputs)
          .map((input) => input.value)
          .join("")
      )
    ) {
      regexErrorFound.push("B");
    }

    return regexErrorFound;
  }

  handleAtcButtonActivity() {
    this.debugMode &&
      console.log("[EngravingInfo] Updating ATC button activity");

    const formWrapper = document.getElementById(this.formId);
    const buttonElements = formWrapper.querySelectorAll(
      this.selectors.addToCartButtons
    );

    const acceleratedCheckoutEls = document.querySelectorAll(
      this.selectors.acceleratedCheckout
    );

    const blankFound = this.checkBlank();
    const regexErrorsFound = this.checkRegex();

    buttonElements.forEach((button) => {
      if (blankFound.length || regexErrorsFound.length) {
        button.classList.add("disabled");
        acceleratedCheckoutEls.forEach((button) =>
          button?.classList.add("hide")
        );
      } else {
        button.classList.remove("disabled");
        acceleratedCheckoutEls.forEach((button) =>
          button?.classList.remove("hide")
        );
      }
    });
  }

  hidePlaceholders(evt) {
    const parentEl = evt.target.parentElement;
    parentEl
      .querySelectorAll("input")
      .forEach((input) => (input.placeholder = ""));
  }

  validateInput() {
    this.debugMode && console.log("[EngravingInfo] ValidateInput Start");
    const blankFound = this.checkBlank();
    this.debugMode &&
      this.debugMode &&
      console.log("[EngravingInfo] Checking for Blanks", { blankFound });
    if (!blankFound.length) {
      this.cancelErrors();
      this.handleAtcButtonActivity();
    }

    this.handleRegexErrors();
  }
}

customElements.define("engraving-info", EngravingInfo);
